import { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE, type InstanceFootprint } from "../document/types.ts";
import { propDrawRow } from "../editor/prop-hit.ts";
import { sortFraction, TIE_STEP } from "../editor/sort.ts";
import { footprintCorner } from "./footprint.ts";

/**
 * Where a character standing on the map is drawn against a placed prop (ADR-132 decisions 2 to 5): the Behind and
 * In front marks a builder painted around a prop, laid on the world's compiled collision cells, with the prop's depth
 * beside each. `standMarks` is the one function that lays them; the editor reads its answer on the open document and
 * the compile bakes the same answer into each chunk.
 */

/**
 * The side of a cell a mark says: a character standing on it is drawn behind the prop.
 */
export const STAND_BEHIND = 1;

/**
 * The side of a cell a mark says: a character standing on it is drawn in front of the prop.
 */
export const STAND_FRONT = 2;

/**
 * One frame's marks and the size they are laid by: the frame in pixels, the row its art meets the ground as the atlas
 * writes it and the renderer draws it (the manifest rect's `base`, never the footprint's), the side in pixels of the cells the marks are counted in (8 or 4) and the marks, `[column,
 * rowsUp]` from the frame's bottom-left.
 */
export interface StandFrame {
  readonly w: number;
  readonly h: number;
  readonly base: number;
  readonly cell: number;
  readonly behind: readonly (readonly [number, number])[];
  readonly front: readonly (readonly [number, number])[];
}

/**
 * A placed prop's own Behind and In front cells (ADR-132 decision 12), `[column, rowsUp]` from its frame's
 * bottom-left in the authoring cell of the world's tile size, as its own footprint holds them. A list that is present
 * replaces the frame's list of that mark, an empty one meaning none; a list left out inherits the frame's.
 */
export interface StandOwnMarks {
  readonly behind?: readonly (readonly [number, number])[];
  readonly front?: readonly (readonly [number, number])[];
}

/**
 * The own marks a placement's footprint gives it: undefined for a prop with no footprint of its own or one that holds
 * blocked cells alone, whose frame's marks apply; otherwise the lists it holds, each as it holds it.
 *
 * @param footprint The placement's own footprint.
 * @returns The own marks, or undefined.
 */
export function ownMarksOf(footprint: InstanceFootprint | undefined): StandOwnMarks | undefined {
  if (footprint === undefined || (footprint.behind === undefined && footprint.front === undefined)) {
    return undefined;
  }
  return { ...(footprint.behind === undefined ? {} : { behind: footprint.behind }), ...(footprint.front === undefined ? {} : { front: footprint.front }) };
}

/**
 * One list of marks a placement lays: the side, the cells and the side in pixels of the cells they are counted in.
 */
interface MarkList {
  readonly side: number;
  readonly cells: readonly (readonly [number, number])[];
  readonly cell: number;
}

/**
 * The lists a placement lays, inherited lists before own ones so an own list decides a cell both mark: each mark's own
 * list in the authoring cell where the placement holds one, else its frame's in the frame's cell.
 *
 * @param frame The frame's marks.
 * @param own The placement's own marks.
 * @param tileSize Tile size in pixels.
 * @returns The lists, empty ones left out.
 */
function markListsOf(frame: StandFrame, own: StandOwnMarks | undefined, tileSize: number): MarkList[] {
  const authoring = tileSize / COLLISION_CELLS_PER_TILE;
  const kinds = [[STAND_BEHIND, frame.behind, own?.behind], [STAND_FRONT, frame.front, own?.front]] as const;
  const inherited = kinds.flatMap(([side, cells, mine]) => (mine === undefined ? [{ side, cells, cell: frame.cell }] : []));
  const owned = kinds.flatMap(([side, , mine]) => (mine === undefined ? [] : [{ side, cells: mine, cell: authoring }]));
  return [...inherited, ...owned].filter((list) => list.cells.length > 0);
}

/**
 * A placed prop as `standMarks` reads it: its frame id, its anchor in tiles, its mirroring, its sort offset in
 * pixels and the depth it draws at (`standPropDepth`). `own` holds the prop's own marks when it has a footprint of
 * its own; they replace its frame's.
 */
export interface StandPlacement {
  readonly frame: string;
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly sortPixels: number;
  readonly depth: number;
  readonly own?: StandOwnMarks;
}

/**
 * The marks of a map as parallel arrays, sorted by cell index (`cy * width + cx` over the compiled collision cells),
 * so equal input gives equal data: the side (`STAND_BEHIND` or `STAND_FRONT`) and the depth of the prop that decides
 * the cell.
 */
export interface StandTable {
  readonly width: number;
  readonly height: number;
  readonly keys: Int32Array;
  readonly sides: Uint8Array;
  readonly depths: Float64Array;
}

/**
 * A lookup over a `StandTable` by world cell. Reads allocate nothing.
 */
export interface StandLookup {
  readonly table: StandTable;
  /**
   * The side marked at a world cell.
   *
   * @param cx Cell column in compiled collision cells.
   * @param cy Cell row.
   * @returns `STAND_BEHIND`, `STAND_FRONT`, or 0 where nothing is marked or the cell lies outside the map.
   */
  sideAt(cx: number, cy: number): number;
  /**
   * The depth of the prop that decides a world cell.
   *
   * @param cx Cell column in compiled collision cells.
   * @param cy Cell row.
   * @returns The depth, or NaN where nothing is marked.
   */
  depthAt(cx: number, cy: number): number;
}

/**
 * A lookup over a table.
 *
 * @param table The table, sorted by key.
 * @returns The lookup.
 */
export function standLookupOf(table: StandTable): StandLookup {
  const index = new Map<number, number>();
  for (let i = 0; i < table.keys.length; i += 1) {
    index.set(table.keys[i] as number, i);
  }
  const find = (cx: number, cy: number): number => (cx < 0 || cy < 0 || cx >= table.width || cy >= table.height ? -1 : (index.get(cy * table.width + cx) ?? -1));
  return {
    table,
    sideAt: (cx, cy) => {
      const at = find(cx, cy);
      return at < 0 ? 0 : (table.sides[at] as number);
    },
    depthAt: (cx, cy) => {
      const at = find(cx, cy);
      return at < 0 ? Number.NaN : (table.depths[at] as number);
    },
  };
}

/**
 * The depth a prop draws at, the one the renderer gives it (`layerDepth + sortFraction(propDrawRow) + order *
 * TIE_STEP`): the layer's depth in the Players band, the row fraction of the row the prop is drawn at, and its tie
 * key. A character given this depth less a hair draws behind the prop; given it plus a hair, in front.
 *
 * @param prop The prop's anchor, vertical flip, sort offset and tie key.
 * @param frame The frame's size and the base row it draws with.
 * @param tileSize Tile size in pixels.
 * @param layerBase The layer's depth (`layerDepth(index, band)`).
 * @param mapHeight Map height in tiles.
 * @returns The depth.
 */
export function standPropDepth(prop: { readonly x: number; readonly y: number; readonly flipY: boolean; readonly sortPixels: number; readonly order: number }, frame: { readonly w: number; readonly h: number; readonly base: number }, tileSize: number, layerBase: number, mapHeight: number): number {
  return layerBase + sortFraction(propDrawRow(prop, { w: frame.w, h: frame.h, base: frame.base }, tileSize), mapHeight) + prop.order * TIE_STEP;
}

/**
 * A prop's base line in world pixels before its sort offset.
 *
 * @param prop The prop's anchor row and vertical flip.
 * @param frame The frame's height and base row.
 * @param tileSize Tile size in pixels.
 * @returns The line.
 */
function baseLineOf(prop: { readonly y: number; readonly flipY: boolean }, frame: { readonly h: number; readonly base: number }, tileSize: number): number {
  return (prop.y + 1) * tileSize - (prop.flipY ? 0 : frame.h - frame.base);
}

/**
 * Lays every placed prop's marks on the world's compiled collision cells and answers for each cell the side and the
 * depth of the prop that decides it. A placement's `own` list of a mark is laid in the authoring cell in place of
 * its frame's list of that mark, and decides a cell its frame's other list also marks; a mark it holds no own list
 * for is laid from the frame. A mark cell of the list's cell size covers the compiled cells it spans; the
 * frame is laid as its footprint is (`footprintCorner`), mirrored across its columns by `flipX` and counted down
 * from its top by `flipY`. Cells outside the map are left out. Where several props mark one cell the one whose base
 * line (with its sort offset) is nearest the cell's middle decides, then the deeper, then the first listed.
 *
 * @param placements The placed props.
 * @param frameOf Looks a frame's marks up; a frame it does not know, or one with no marks, adds nothing.
 * @param tileSize Tile size in pixels.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @returns The lookup.
 */
export function standMarks(placements: readonly StandPlacement[], frameOf: (frame: string) => StandFrame | undefined, tileSize: number, width: number, height: number): StandLookup {
  const px = tileSize / COMPILED_COLLISION_CELLS_PER_TILE;
  const cellsWide = width * COMPILED_COLLISION_CELLS_PER_TILE;
  const cellsHigh = height * COMPILED_COLLISION_CELLS_PER_TILE;
  const best = new Map<number, { distance: number; depth: number; side: number }>();
  for (const placement of placements) {
    const frame = frameOf(placement.frame);
    const lists = frame === undefined ? [] : markListsOf(frame, placement.own, tileSize);
    if (frame === undefined || lists.length === 0) {
      continue;
    }
    const line = baseLineOf(placement, frame, tileSize) + placement.sortPixels;
    const corner = footprintCorner(placement.x * tileSize, (placement.y + 1) * tileSize, frame.w, placement.flipX, px);
    const top = corner.bottom - frame.h;
    const laid = new Map<number, number>();
    for (const { side, cells, cell } of lists) {
      const columns = Math.ceil(frame.w / cell);
      const left = placement.flipX ? corner.left + frame.w - columns * cell : corner.left;
      for (const [column, up] of cells) {
        const x0 = left + (placement.flipX ? columns - 1 - column : column) * cell;
        const y0 = placement.flipY ? top + up * cell : corner.bottom - (up + 1) * cell;
        const cx0 = Math.max(0, Math.floor(x0 / px));
        const cx1 = Math.min(cellsWide - 1, Math.floor((x0 + cell - 1) / px));
        const cy0 = Math.max(0, Math.floor(y0 / px));
        const cy1 = Math.min(cellsHigh - 1, Math.floor((y0 + cell - 1) / px));
        for (let cy = cy0; cy <= cy1; cy += 1) {
          for (let cx = cx0; cx <= cx1; cx += 1) {
            laid.set(cy * cellsWide + cx, side);
          }
        }
      }
    }
    for (const [key, side] of laid) {
      const distance = Math.abs(Math.floor(key / cellsWide) * px + px / 2 - line);
      const held = best.get(key);
      if (held === undefined || distance < held.distance || (distance === held.distance && placement.depth > held.depth)) {
        best.set(key, { distance, depth: placement.depth, side });
      }
    }
  }
  const keys = Int32Array.from([...best.keys()].sort((a, b) => a - b));
  const sides = new Uint8Array(keys.length);
  const depths = new Float64Array(keys.length);
  keys.forEach((key, i) => {
    const entry = best.get(key) as { depth: number; side: number };
    sides[i] = entry.side;
    depths[i] = entry.depth;
  });
  return standLookupOf({ width: cellsWide, height: cellsHigh, keys, sides, depths });
}
